import React from 'react';
import { T } from '../../designTokens';

interface PageButtonProps {
  label: string;
  title?: string;
  ariaLabel?: string;
  disabled?: boolean;
  active?: boolean;
  onClick: () => void;
}

export function PageButton({ label, title, ariaLabel, disabled = false, active = false, onClick }: PageButtonProps) {
  return (
    <button
      title={title}
      aria-label={ariaLabel ?? title}
      aria-current={active ? 'page' : undefined}
      disabled={disabled}
      onClick={onClick}
      style={{
        minWidth: '32px',
        height: '32px',
        padding: '0 8px',
        fontSize: '14px',
        fontWeight: active ? 700 : 400,
        color: active ? T.white : disabled ? T.gray300 : T.gray700,
        backgroundColor: active ? T.cobalt600 : T.white,
        border: `1px solid ${active ? T.cobalt600 : disabled ? T.gray200 : T.gray300}`,
        borderRadius: `${T.radiusMd}px`,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.15s',
        lineHeight: '1',
      }}
    >
      {label}
    </button>
  );
}

/**
 * Up to 7 page-number buttons with ellipsis for large page counts.
 * Always shows first + last; shows a window around the current page.
 */
export function PageNumberButtons({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  const pages: (number | '…')[] = [];

  if (totalPages <= 7) {
    for (let p = 1; p <= totalPages; p++) pages.push(p);
  } else {
    pages.push(1);
    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);
    if (windowStart > 2) pages.push('…');
    for (let p = windowStart; p <= windowEnd; p++) pages.push(p);
    if (windowEnd < totalPages - 1) pages.push('…');
    pages.push(totalPages);
  }

  return (
    <>
      {pages.map((p, idx) =>
        p === '…' ? (
          <span
            key={`ellipsis-${idx}`}
            style={{ padding: '0 4px', color: T.gray400, fontSize: '14px', lineHeight: '32px' }}
          >
            …
          </span>
        ) : (
          <PageButton
            key={p}
            label={String(p)}
            ariaLabel={`Go to page ${p}`}
            active={p === currentPage}
            onClick={() => onPageChange(p)}
          />
        )
      )}
    </>
  );
}

interface PaginationProps {
  page: number;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  onPageChange: (p: number) => void;
  onPageSizeChange: (size: number) => void;
}

export default function Pagination({
  page,
  totalPages,
  rangeStart,
  rangeEnd,
  total,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: T.gray500, fontSize: '14px' }}>
        <span>
          {rangeStart}–{rangeEnd} of {total}
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          Rows:
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            style={{
              padding: '4px 8px',
              fontSize: '13px',
              border: `1px solid ${T.gray300}`,
              borderRadius: `${T.radiusMd}px`,
              backgroundColor: T.white,
              color: T.gray700,
              cursor: 'pointer',
            }}
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <PageButton label="«" title="First page" disabled={page === 1} onClick={() => onPageChange(1)} />
        <PageButton label="‹" title="Previous page" disabled={page === 1} onClick={() => onPageChange(Math.max(1, page - 1))} />
        <PageNumberButtons currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        <PageButton label="›" title="Next page" disabled={page === totalPages} onClick={() => onPageChange(Math.min(totalPages, page + 1))} />
        <PageButton label="»" title="Last page" disabled={page === totalPages} onClick={() => onPageChange(totalPages)} />
      </div>
    </div>
  );
}
